import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaDriversLicenseGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Driver&apos;s License Guide</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Driver&apos;s License Guide</h1>
        <p className="text-obsidian-300">Complete guide to obtaining, renewing, and managing your Nevada driver&apos;s license.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Nevada Driver&apos;s License Overview</h2>
          <p>Nevada issues driver&apos;s licenses through the Department of Motor Vehicles. New residents must obtain a Nevada driver&apos;s license within <strong>30 days</strong> of establishing residency. Nevada licenses are valid for <strong>8 years</strong> for most drivers.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">License Classes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { class: 'Class C', desc: 'Standard passenger vehicle license. For vehicles under 26,001 lbs not requiring a CDL.' },
              { class: 'Class A CDL', desc: 'For combination vehicles with a GVWR over 26,001 lbs, towing over 10,001 lbs.' },
              { class: 'Class B CDL', desc: 'For single vehicles over 26,001 lbs, or towing 10,001 lbs or less.' },
              { class: 'Class C CDL', desc: 'For vehicles carrying 16+ passengers or transporting hazardous materials.' },
              { class: 'Motorcycle (M)', desc: 'Endorsement added to existing license for motorcycle operation.' },
              { class: 'REAL ID', desc: 'Federal compliant ID for boarding domestic flights and accessing federal facilities.' },
            ].map(item => (
              <div key={item.class} className="border border-obsidian-100 rounded-lg p-4">
                <span className="inline-block bg-obsidian-900 text-gold-400 text-xs font-bold px-2 py-0.5 rounded mb-2">{item.class}</span>
                <p className="text-obsidian-600 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Required Documents (New License)</h2>
          <ul className="space-y-3">
            {[
              'Proof of identity (U.S. passport, birth certificate, or other accepted document)',
              'Proof of Social Security Number (Social Security card, W-2, or pay stub)',
              'Two proofs of Nevada residency (utility bill, bank statement, lease agreement)',
              'Proof of legal presence in the United States',
              'Payment for license fees',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Testing Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Knowledge Test', desc: 'Written test on Nevada traffic laws and road signs. Must score 80% or higher.' },
              { title: 'Vision Test', desc: 'Basic vision screening at the DMV office. Corrective lenses noted on license if needed.' },
              { title: 'Skills Test', desc: 'Behind-the-wheel driving test for new drivers. Must provide a vehicle for the test.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4 text-center">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need DMV Assistance?</h3>
          <p className="text-obsidian-600 text-sm mb-4">While Speedy DMV Services primarily handles vehicle transactions, we can guide you through the driver&apos;s license process.</p>
          <Link to="/contact" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Contact Us</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaDriversLicenseGuidePage;
